import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, Lock } from 'lucide-react';

const ChooseReportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedTier, setSelectedTier] = useState('premium');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Get data from navigation state (passed from questionnaire)
  const { questionnaireData, childName, childId } = location.state || {};

  const calculatePricing = useCallback(async (mobileNumber, reportType) => {
    try {
      const response = await fetch(`${API_URL}/api/parent/calculate-pricing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_mobile: mobileNumber,
          child_id: childId || null,
          report_type: reportType,
          is_upgrade: false
        })
      });

      const data = await response.json();
      setPricing(data);
    } catch (err) {
      console.error('Failed to calculate pricing:', err);
      // Use default pricing on error
    } finally {
      setLoading(false);
    }
  }, [API_URL, childId]);

  useEffect(() => {
    const mobileNumber = localStorage.getItem('parent_mobile');
    
    if (!mobileNumber) {
      navigate('/parent-login');
      return;
    }

    if (!questionnaireData) {
      navigate('/questionnaire');
      return;
    }

    calculatePricing(mobileNumber, 'premium');
  }, [navigate, questionnaireData, calculatePricing]);

  const handlePurchase = async () => {
    setProcessing(true);
    const mobileNumber = localStorage.getItem('parent_mobile');

    try {
      const purchaseType = selectedTier === 'premium' 
        ? (pricing?.is_returning_user ? 'new_child_premium' : 'first_child_premium')
        : (pricing?.is_returning_user ? 'new_child_basic' : 'first_child_basic');

      const response = await fetch(`${API_URL}/api/parent/purchase-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_mobile: mobileNumber,
          child_id: childId || `child_${Date.now()}`,
          child_name: childName || 'My Child',
          purchase_type: purchaseType,
          report_type: selectedTier,
          questionnaire_data: questionnaireData
        })
      });

      const data = await response.json();

      if (data.success) {
        // Navigate to report page
        navigate(`/parent-report/${data.report_id}`, {
          state: { reportData: data.report_data }
        });
      } else {
        alert('Purchase failed. Please try again.');
      }
    } catch (err) {
      alert('Network error. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const basicPrice = selectedTier === 'basic' ? (pricing?.amount || 4.99) : 4.99;
  const premiumPrice = selectedTier === 'premium' ? (pricing?.amount || 14.99) : 14.99;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Choose Your Report
          </h1>
          <p className="text-lg text-gray-600">
            Select the report tier that best fits your needs
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Basic Tier */}
          <div
            onClick={() => setSelectedTier('basic')}
            className={`bg-white rounded-2xl shadow-lg p-8 cursor-pointer transition-all border-2 ${
              selectedTier === 'basic'
                ? 'border-blue-600 shadow-xl scale-105'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Basic</h3>
                <p className="text-gray-600 text-sm mt-1">Perfect for initial exploration</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-600">
                  RM {basicPrice.toFixed(2)}
                </div>
              </div>
            </div>

            <ul className="space-y-4 mb-6">
              <li className="flex items-start gap-3">
                <Check className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <span className="text-gray-700">
                  <strong>1 Top School Match</strong> based on your preferences
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <span className="text-gray-700">
                  Match score & compatibility breakdown
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <span className="text-gray-700">
                  Key school details (fees, curriculum, location)
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Lock className="text-gray-400 flex-shrink-0 mt-0.5" size={20} />
                <span className="text-gray-400 line-through">
                  3 near-miss alternatives
                </span>
              </li>
            </ul>

            {selectedTier === 'basic' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                <p className="text-sm text-blue-800 font-medium">Selected</p>
              </div>
            )}
          </div>

          {/* Premium Tier */}
          <div
            onClick={() => setSelectedTier('premium')}
            className={`bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl shadow-lg p-8 cursor-pointer transition-all border-2 relative ${
              selectedTier === 'premium'
                ? 'border-yellow-400 shadow-2xl scale-105'
                : 'border-purple-600 hover:border-purple-700'
            }`}
          >
            {/* Recommended Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-yellow-400 text-gray-900 text-xs font-bold px-4 py-1 rounded-full shadow-md">
                RECOMMENDED
              </span>
            </div>

            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-white">Premium</h3>
                <p className="text-purple-100 text-sm mt-1">Complete school discovery</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-white">
                  RM {premiumPrice.toFixed(2)}
                </div>
              </div>
            </div>

            <ul className="space-y-4 mb-6">
              <li className="flex items-start gap-3">
                <Check className="text-yellow-300 flex-shrink-0 mt-0.5" size={20} />
                <span className="text-white">
                  <strong>3 Top School Matches</strong> ranked by compatibility
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-yellow-300 flex-shrink-0 mt-0.5" size={20} />
                <span className="text-white">
                  Detailed match scores & compatibility breakdown
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-yellow-300 flex-shrink-0 mt-0.5" size={20} />
                <span className="text-white">
                  Complete school profiles (fees, curriculum, reviews)
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-yellow-300 flex-shrink-0 mt-0.5" size={20} />
                <span className="text-white font-semibold">
                  3 Near-Miss Alternatives (slightly out of range)
                </span>
              </li>
            </ul>

            {selectedTier === 'premium' && (
              <div className="bg-white/20 border border-white/40 rounded-lg p-3 text-center backdrop-blur-sm">
                <p className="text-sm text-white font-medium">Selected</p>
              </div>
            )}
          </div>
        </div>

        {/* Purchase Button */}
        <div className="text-center">
          <button
            onClick={handlePurchase}
            disabled={processing}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg py-4 px-12 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processing ? (
              <span className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Processing Payment...
              </span>
            ) : (
              `Continue to Payment (RM ${selectedTier === 'premium' ? premiumPrice.toFixed(2) : basicPrice.toFixed(2)})`
            )}
          </button>

          <p className="text-sm text-gray-500 mt-4">
            💳 Mock Payment · Instant Report · Upgrade Anytime
          </p>
        </div>

        {/* Re-run / Upgrade Pricing Info */}
        {pricing?.is_returning_user && (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h4 className="font-semibold text-gray-900 mb-3">Returning User Benefits</h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>✅ Re-run Basic: <strong>RM 4.99</strong></li>
              <li>✅ Re-run Premium: <strong>RM 10.99</strong> (Returning User Price · Regular: RM 14.99)</li>
              <li>✅ Upgrade Basic → Premium: <strong>+RM 10.00</strong></li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChooseReportPage;
